import React from "react";
import type {
  EditGoogleState,
  EditNamedCounts,
  ProfileEditOutcome,
  ProfileEditOutcomes as ProfileEditOutcomesBlock,
} from "../../../services/visibilityService";
import { isRecord } from "../profileFindingsModel";
import { formatValue, isHours, isOpeningDate } from "../../../components/AIAgents/ChatWithAgent/utils/gbpProposalText";
import { GBP_PHOTO_CATEGORIES } from "../../../services/gbpConstants";

/**
 * "Changes you made" on the AI Visibility dashboard (SGL-328 #2): each
 * Business Profile change the owner applied, where it stands on Google now,
 * and how often AI named the business before and after it.
 *
 * Rendered in the "You & your Visibility Manager" card (no second dashboard),
 * inside `.avm-scope`, so it uses that design system's tokens; own classes are
 * prefixed `avm-eo-`.
 *
 * Honesty rules:
 *   - Counts are AI answers (one question put to one engine), the same unit as
 *     "AI answers checked" on this page, and always say "AI answers".
 *   - Before/after is shown side by side; nothing here says the change caused
 *     the difference.
 *   - "Google changed it back" and "isn't showing on your listing" are shown
 *     as problems; nothing re-applies a change.
 *   - A block we could not compute says so in one line; it is never an empty
 *     list. "no_edits" and "not_a_customer" render nothing.
 */

/** Newest first, this many at most; the rest are counted. */
export const OUTCOMES_SHOWN = 5;

const FIELD_TITLES: Record<string, string> = {
  services: "Services",
  description: "Description",
  website: "Website",
  phone: "Phone",
  additional_categories: "Additional categories",
  primary_category: "Main category",
  regular_hours: "Opening hours",
  opening_date: "Opening date",
  attributes: "Business details",
  photo: "Photo",
};

export const fieldTitle = (field: string): string => FIELD_TITLES[field] ?? "Profile change";

/** "categories/gcid:dry_cleaner" -> "Dry cleaner". The log stores Google's id,
 *  not its display name, so this is the id made readable. */
function humanId(id: string): string {
  const bare = id.replace(/^categories\/gcid:/, "").replace(/^attributes\//, "").replace(/_/g, " ").trim();
  return bare ? bare[0].toUpperCase() + bare.slice(1) : id;
}

const isCategoryId = (v: unknown): v is string => typeof v === "string" && v.startsWith("categories/gcid:");

/** A logged value in words, or "" when there is nothing to show. */
export function valueText(field: string, v: unknown): string {
  if (v === null || v === undefined) return "";
  if (field === "photo") {
    const cat = isRecord(v) && typeof v.category === "string" ? v.category : "";
    const label = GBP_PHOTO_CATEGORIES.find((c) => c.value === cat)?.label;
    return label ? `A photo (${label.toLowerCase()})` : "A photo";
  }
  if (field === "attributes" && isRecord(v)) {
    const name = typeof v.attribute === "string" ? humanId(v.attribute) : "";
    const value = typeof v.value === "boolean" ? (v.value ? "Yes" : "No") : typeof v.value === "string" ? humanId(v.value) : "";
    // No value = the attribute was not set (the usual `old`): nothing to strike out.
    return name && value ? `${name}: ${value}` : "";
  }
  if (isCategoryId(v)) return humanId(v);
  if (Array.isArray(v) && v.every((x) => typeof x === "string")) {
    return (v as string[]).map((x) => (isCategoryId(x) ? humanId(x) : x)).join(", ");
  }
  if (typeof v === "string" || isHours(v) || isOpeningDate(v)) return formatValue(v);
  return "";
}

const GOOGLE_STATE: Record<EditGoogleState, { label: string; tone: "ok" | "mid" | "no" | "none" }> = {
  still_on_google: { label: "Still on Google", tone: "ok" },
  changed_back: { label: "Google changed it back", tone: "no" },
  not_on_google: { label: "Sent to Google, but it isn't showing on your listing", tone: "no" },
  pending_review: { label: "Google is reviewing it", tone: "mid" },
  superseded: { label: "Replaced by a newer change", tone: "none" },
  unknown: { label: "We can't check this one on Google", tone: "none" },
};

export function googleStateLabel(state: unknown): { label: string; tone: "ok" | "mid" | "no" | "none" } {
  return typeof state === "string" && state in GOOGLE_STATE ? GOOGLE_STATE[state as EditGoogleState] : GOOGLE_STATE.unknown;
}

const count = (c: EditNamedCounts | null | undefined): [number, number] | null =>
  c && typeof c.answers_named === "number" && typeof c.answers_checked === "number" && c.answers_checked > 0
    ? [c.answers_named, c.answers_checked]
    : null;

const NOT_COMPARABLE: Record<string, string> = {
  prompt_set_changed: "Not directly comparable: the questions asked changed between the two checks.",
  engine_set_changed: "Not directly comparable: a different set of AI engines answered.",
  prompt_set_unknown: "Not directly comparable: we can't tell whether the same questions were asked.",
};

/** The before/after sentence, and a caveat when the two checks differ. */
export function resultText(o: ProfileEditOutcome): { text: string; caveat: string | null } {
  if (o.result === "too_soon") return { text: "We'll know after the next check.", caveat: null };
  if (o.result === "no_baseline") return { text: "No earlier check to compare.", caveat: null };
  const after = count(o.named_after);
  const before = count(o.named_before);
  if (!after || !before) return { text: "We couldn't read the numbers for these checks.", caveat: null };
  const caveat = o.comparable === true ? null : NOT_COMPARABLE[o.not_comparable_reason ?? ""] ?? NOT_COMPARABLE.prompt_set_unknown;
  return {
    text: `Named in ${after[0]} of ${after[1]} AI answers, was ${before[0]} of ${before[1]}.`,
    caveat,
  };
}

function appliedDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

const isOutcome = (v: unknown): v is ProfileEditOutcome =>
  isRecord(v) && typeof v.field === "string" && typeof v.applied_at === "string" && typeof v.result === "string";

const EO_STYLES = `
.avm-scope .avm-eo-list { display: flex; flex-direction: column; }
.avm-scope .avm-eo-item { padding: 12px 2px; }
.avm-scope .avm-eo-item + .avm-eo-item { border-top: 1px solid var(--avm-line); }
.avm-scope .avm-eo-head { display: flex; justify-content: space-between; gap: 12px; align-items: baseline; flex-wrap: wrap; }
.avm-scope .avm-eo-title { font-weight: 600; font-size: 14px; }
.avm-scope .avm-eo-date { font-size: 12px; color: var(--avm-muted); }
.avm-scope .avm-eo-swap { margin: 6px 0 0; font-size: 13px; display: flex; flex-wrap: wrap; align-items: center; gap: 6px; }
.avm-scope .avm-eo-chip { display: inline-block; max-width: 100%; overflow-wrap: anywhere; font-size: 12px; font-weight: 600; padding: 2px 9px; border-radius: 999px; background: var(--avm-surface-2); border: 1px solid var(--avm-line); color: var(--avm-ink); }
.avm-scope .avm-eo-chip.old { color: var(--avm-muted); text-decoration: line-through; }
.avm-scope .avm-eo-chip.new { background: var(--avm-primary-soft); border-color: transparent; color: var(--avm-primary); }
.avm-scope .avm-eo-state { margin: 6px 0 0; font-size: 13px; display: flex; align-items: center; gap: 7px; }
.avm-scope .avm-eo-state.no { color: var(--avm-bad); font-weight: 600; }
.avm-scope .avm-eo-dot { width: 8px; height: 8px; border-radius: 50%; flex: none; background: var(--avm-faint); }
.avm-scope .avm-eo-dot.ok { background: var(--avm-good); }
.avm-scope .avm-eo-dot.mid { background: var(--avm-warn); }
.avm-scope .avm-eo-dot.no { background: var(--avm-bad); }
.avm-scope .avm-eo-result { margin: 4px 0 0; font-size: 13px; }
.avm-scope .avm-eo-caveat { margin: 2px 0 0; font-size: 12px; color: var(--avm-muted); }
.avm-scope .avm-eo-line { margin: 0; font-size: 13.5px; color: var(--avm-muted); }
.avm-scope .avm-eo-sr { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0; }
`;

/** Fields whose edit ADDS entries: the log's `old` is everything on the
 *  profile before, which the edit kept, so it is never shown struck out. */
const ADDITIVE_FIELDS = new Set(["services", "additional_categories"]);

const OutcomeItem: React.FC<{ o: ProfileEditOutcome }> = ({ o }) => {
  const additive = ADDITIVE_FIELDS.has(o.field);
  const oldText = additive ? "" : valueText(o.field, o.old);
  const newText = valueText(o.field, o.new);
  const state = googleStateLabel(o.google_state);
  const { text, caveat } = resultText(o);
  const date = appliedDate(o.applied_at);
  return (
    <li className="avm-eo-item">
      <div className="avm-eo-head">
        <span className="avm-eo-title">{fieldTitle(o.field)}</span>
        {date && <span className="avm-eo-date">Changed {date}</span>}
      </div>
      {newText && (
        <p className="avm-eo-swap">
          {oldText && (
            <>
              <span className="avm-eo-chip old">
                <span className="avm-eo-sr">Was </span>
                {oldText}
              </span>
              <span aria-hidden="true">→</span>
            </>
          )}
          <span className="avm-eo-chip new">
            {additive ? "Added: " : <span className="avm-eo-sr">Now </span>}
            {newText}
          </span>
        </p>
      )}
      <p className={`avm-eo-state ${state.tone === "no" ? "no" : ""}`}>
        <span className={`avm-eo-dot ${state.tone}`} aria-hidden="true" />
        {state.label}
      </p>
      <p className="avm-eo-result">{text}</p>
      {caveat && <p className="avm-eo-caveat">{caveat}</p>}
    </li>
  );
};

/** What to render, or null for "render nothing". */
function content(value: ProfileEditOutcomesBlock | null | undefined, checked: boolean): React.ReactNode | null {
  if (!isRecord(value)) {
    // Not computed for this run (an older run, or the step failed). Only worth
    // a line once there is a run to speak of.
    return checked ? <p className="avm-eo-line">Changes you make to your Business Profile will show here after your next check.</p> : null;
  }
  switch (value.status) {
    case "ok": {
      const all = (Array.isArray(value.outcomes) ? value.outcomes : []).filter(isOutcome);
      if (!all.length) return null;
      const shown = all.slice(0, OUTCOMES_SHOWN);
      const more = all.length - shown.length;
      return (
        <>
          <ul className="avm-eo-list">
            {shown.map((o) => (
              <OutcomeItem key={`${o.applied_at}-${o.field}-${o.location_id ?? ""}`} o={o} />
            ))}
          </ul>
          {more > 0 && <p className="avm-eo-line">And {more} earlier {more === 1 ? "change" : "changes"}.</p>}
        </>
      );
    }
    case "edit_log_failed":
      return <p className="avm-eo-line">We couldn't read the changes you made this time; the next check will try again.</p>;
    case "insufficient_coverage":
      return (
        <p className="avm-eo-line">
          Too few AI answers came back in this check to compare your changes; the next check will try again.
        </p>
      );
    default:
      // no_edits, not_a_customer, or a status added later.
      return null;
  }
}

export function hasEditOutcomesContent(value: ProfileEditOutcomesBlock | null | undefined, checked = true): boolean {
  return content(value, checked) !== null;
}

/** True when there is at least one change to list (not just a one-line status). */
export function hasEditOutcomeList(value: ProfileEditOutcomesBlock | null | undefined): boolean {
  return isRecord(value) && value.status === "ok" && Array.isArray(value.outcomes) && value.outcomes.some(isOutcome);
}

const ProfileEditOutcomes: React.FC<{ value: ProfileEditOutcomesBlock | null | undefined; checked: boolean }> = ({
  value,
  checked,
}) => {
  const body = content(value, checked);
  if (body === null) return null;
  return (
    <section aria-label="Changes you made">
      <style>{EO_STYLES}</style>
      <p className="avm-eyebrow" style={{ margin: "0 0 6px" }}>Changes you made to your Google Business Profile</p>
      {body}
    </section>
  );
};

export default ProfileEditOutcomes;
